import React from 'react';
import {
    Badge,
    Box,
    Divider,
    Grid,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FiCheckCircle, FiClock, FiShield } from 'react-icons/fi';

// Every "Implemented" line must be true of production today; see
// B2B/compliance/GDPR_GAP_ANALYSIS_2026-09-26.md before changing one.
const IMPLEMENTED = [
    {
        title: 'Tenant isolation',
        detail: 'PostgreSQL row-level security with per-tenant policy enforcement and advisory locks during setup.',
        evidence: 'docs/TENANT_ISOLATION_VERIFICATION.md',
    },
    {
        title: 'Authentication',
        detail: 'JWT RS256 with keys held in Secret Manager and rotated at least once a year (last rotation: September 2026). Role-based access for Admin, RM, Head, Portfolio Adviser, Compliance, View Only.',
        evidence: 'docs/PRODUCTION_RS256_SETUP.md',
    },
    {
        title: 'Audit logging',
        detail: 'Append-only audit log on uploads, exports, admin actions and data-protection requests, enforced by the database: entries cannot be edited or deleted. Erasure and retention may only redact client identifiers inside an entry, and each redaction is itself logged. Exportable for supervisory review.',
        evidence: 'AuditCompliance console',
    },
    {
        title: 'Data residency',
        detail: 'EU only. Client data, user accounts, backups and application logs are stored in EU regions: Google Cloud Madrid, Belgium and Frankfurt, and MongoDB Atlas in the EU. No data leaves the EU without an explicit DPA addendum.',
        evidence: 'Sub-processor list in the DPA',
    },
    {
        title: 'Client data requests',
        detail: 'Admin and Compliance can export or erase one client\'s records from this page. Raw upload files are cleared 30 days after upload.',
        evidence: 'Data-protection requests, below',
    },
    {
        title: 'API perimeter',
        detail: 'OpenAPI surface gated. Developer portal requires Business entitlement. Anonymous documentation endpoints disabled in production.',
        evidence: 'ENABLE_OPENAPI_DOCS=false in prod',
    },
    {
        title: 'Data processing addendum',
        detail: 'DPA available on request: controller/processor scope, pseudonymisation, retention, security measures and sub-processor list.',
        evidence: 'On request',
    },
];

const ROADMAP = [
    {
        title: 'SOC 2 Type I',
        detail: 'Formal Type I attestation workstreams in progress: joiner/mover/leaver, privileged access review, secret rotation cadence, vendor risk register.',
        evidence: 'docs/SOC2_TYPE1_ROADMAP.md',
    },
    {
        title: 'External penetration testing',
        detail: 'Annual third-party pen-test cadence planned alongside the SOC 2 timeline.',
        evidence: 'Roadmap',
    },
    {
        title: 'Customer-managed encryption keys',
        detail: 'CMEK option for tenants requiring key custody separation. Currently using GCP-managed encryption at rest.',
        evidence: 'Roadmap',
    },
];

const SecurityPostureCard = ({
    cardBg = 'white',
    borderColor = 'gray.200',
    headingTextColor = 'gray.800',
    bodyTextColor = 'gray.600',
    mutedTextColor = 'gray.500',
}) => {
    return (
        <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            p={{ base: 4, md: 5 }}
            boxShadow="sm"
        >
            <HStack spacing={3} mb={4}>
                <Icon as={FiShield} color="brand.navy" boxSize={5} />
                <Heading size="sm" color={headingTextColor}>
                    Security and compliance posture
                </Heading>
                <Badge colorScheme="purple" borderRadius="full" px={2}>
                    Disclosure
                </Badge>
            </HStack>
            <Text fontSize="sm" color={bodyTextColor} mb={4}>
                What FinanSee implements today versus what is on the roadmap. Items marked as implemented are
                live in this environment. We do not claim certifications we do not hold; SOC 2 is a roadmap
                workstream, not an active attestation.
            </Text>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
                <Box>
                    <HStack mb={3}>
                        <Icon as={FiCheckCircle} color="green.500" />
                        <Text fontSize="xs" textTransform="uppercase" fontWeight="bold" color="green.700">
                            Implemented
                        </Text>
                    </HStack>
                    <VStack align="stretch" spacing={3}>
                        {IMPLEMENTED.map((item) => (
                            <Box
                                key={item.title}
                                borderWidth="1px"
                                borderColor="green.100"
                                bg="green.50"
                                borderRadius="md"
                                p={3}
                            >
                                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>
                                    {item.title}
                                </Text>
                                <Text fontSize="xs" color={bodyTextColor} mt={1}>
                                    {item.detail}
                                </Text>
                                {item.evidence && (
                                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                                        Evidence: {item.evidence}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </VStack>
                </Box>

                <Box>
                    <HStack mb={3}>
                        <Icon as={FiClock} color="orange.500" />
                        <Text fontSize="xs" textTransform="uppercase" fontWeight="bold" color="orange.700">
                            Roadmap
                        </Text>
                    </HStack>
                    <VStack align="stretch" spacing={3}>
                        {ROADMAP.map((item) => (
                            <Box
                                key={item.title}
                                borderWidth="1px"
                                borderColor="orange.100"
                                bg="orange.50"
                                borderRadius="md"
                                p={3}
                            >
                                <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>
                                    {item.title}
                                </Text>
                                <Text fontSize="xs" color={bodyTextColor} mt={1}>
                                    {item.detail}
                                </Text>
                                {item.evidence && (
                                    <Text fontSize="xs" color={mutedTextColor} mt={1}>
                                        Evidence: {item.evidence}
                                    </Text>
                                )}
                            </Box>
                        ))}
                    </VStack>
                </Box>
            </Grid>

            <Divider my={4} borderColor={borderColor} />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <Box>
                    <Text fontSize="xs" color={mutedTextColor}>Last reviewed</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>2026-09-26</Text>
                </Box>
                <Box>
                    <Text fontSize="xs" color={mutedTextColor}>Disclosure owner</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>FinanSee Compliance</Text>
                </Box>
                <Box>
                    <Text fontSize="xs" color={mutedTextColor}>For the DPA and sub-processor list</Text>
                    <Text fontSize="sm" fontWeight="semibold" color={headingTextColor}>compliance@finansee.eu</Text>
                </Box>
            </SimpleGrid>
        </Box>
    );
};

export default SecurityPostureCard;
